/**
 * Custom MUI theme — Career Forge.
 *
 * Design system (Phase 1 — coral-forward / navy-identity):
 * - Coral primary (action color: CTAs, links, active states) — WCAG AA per spec R1
 * - Navy `identity` slot (logo, institutional tooltips, rare marketing panels only)
 * - Green/amber/red for status & feedback
 * - Elevated card shadows with depth hierarchy
 * - Inter typeface, DESIGN_BRIEF §5 Linear scale (two weights 400/500)
 * - 8px rhythm across all spacing
 *
 * Token VALUES live in ./tokens.ts (single source of truth).
 * Supports light + dark modes via buildTheme(mode).
 */

import { createTheme, alpha, type PaletteMode } from "@mui/material/styles";
import { navy, coral, green, product, coralRoles, tints } from "./tokens";

const SHADOW_SM = "0 1px 2px 0 rgba(0,0,0,0.05)";
const SHADOW_MD =
  "0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -2px rgba(0,0,0,0.05)";
const SHADOW_LG =
  "0 10px 15px -3px rgba(0,0,0,0.08), 0 4px 6px -4px rgba(0,0,0,0.04)";
const SHADOW_XL =
  "0 20px 25px -5px rgba(0,0,0,0.08), 0 8px 10px -6px rgba(0,0,0,0.04)";

// Dark mode shadow variants — slightly more diffused for dark backgrounds
const SHADOW_SM_DARK = "0 1px 3px 0 rgba(0,0,0,0.25)";
const SHADOW_MD_DARK =
  "0 4px 8px -1px rgba(0,0,0,0.3), 0 2px 4px -2px rgba(0,0,0,0.2)";
const SHADOW_LG_DARK =
  "0 10px 20px -3px rgba(0,0,0,0.35), 0 4px 8px -4px rgba(0,0,0,0.2)";
const SHADOW_XL_DARK =
  "0 20px 30px -5px rgba(0,0,0,0.4), 0 8px 12px -6px rgba(0,0,0,0.25)";

// ---------------------------------------------------------------------------
// Theme builder
// ---------------------------------------------------------------------------

export function buildTheme(mode: PaletteMode) {
  const isDark = mode === "dark";
  const c = coralRoles(mode);

  const shadowSm = isDark ? SHADOW_SM_DARK : SHADOW_SM;
  const shadowMd = isDark ? SHADOW_MD_DARK : SHADOW_MD;
  const shadowLg = isDark ? SHADOW_LG_DARK : SHADOW_LG;
  const shadowXl = isDark ? SHADOW_XL_DARK : SHADOW_XL;

  return createTheme({
    palette: {
      mode,
      // Coral is the action color now (spec D1). main = coral-as-text (mode-aware,
      // AA); dark = text-hover; light = decorative fill. Contained-button bg is
      // forced to coral-600 in both modes via the containedPrimary override below
      // (white text 4.92:1, spec R1).
      primary: {
        main: c.text,
        dark: c.textHover,
        light: c.fill,
        contrastText: "#ffffff",
      },
      // `secondary` kept as coral for backward-compat with the ~15 existing
      // `color="secondary"` call-sites until the migration (Tasks 4–8) consolidates
      // them onto `primary`. Mirrors primary so no untouched component regresses.
      secondary: {
        main: c.text,
        dark: c.textHover,
        light: c.fill,
        contrastText: "#ffffff",
      },
      // Navy is identity-only now (spec D1): logo, institutional tooltips, rare
      // marketing panels. Not the action color.
      identity: {
        main: isDark ? navy[400] : navy[500],
        dark: navy[700],
        contrastText: "#ffffff",
      },
      coralFill: c.fill,
      product, // per-product accent tokens — defined, not applied (spec D2)
      tint: tints(mode),
      error: {
        main: isDark ? "#fc8181" : "#e53e3e",
        light: "#fc8181",
        dark: "#c53030",
      },
      warning: {
        main: isDark ? "#f6e05e" : "#d69e2e",
        light: "#f6e05e",
        dark: "#b7791f",
      },
      info: {
        main: isDark ? "#63b3ed" : "#3182ce",
        light: "#63b3ed",
        dark: "#2c5282",
      },
      success: {
        main: green[500],
        light: green[200],
        dark: green[700],
      },
      background: {
        default: isDark ? "#0d1117" : "#f7fafc",
        paper: isDark ? "#161b22" : "#ffffff",
      },
      text: {
        primary: isDark ? "#e6edf3" : "#1a202c",
        // Light secondary darkened #718096 (~4.4:1) → #4a5568 (~7.5:1 on white) for AA/AAA legibility.
        secondary: isDark ? "#8b949e" : "#4a5568",
        // Explicit disabled: MUI default (~2.8:1) fails AA. Light #64748b (~4.8:1 on white),
        // dark #768390 (~4.9:1 on #0d1117) — both pass AA for normal text.
        disabled: isDark ? "#768390" : "#64748b",
      },
      divider: isDark
        ? alpha("#e6edf3", 0.08)
        : alpha("#1a365d", 0.08),
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica Neue", Arial, sans-serif',
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 500, // brief: no 600/700/800
      h1: { fontWeight: 500, fontSize: "clamp(2.25rem, 5vw, 3.5rem)", lineHeight: 1.2, letterSpacing: "-0.02em" },
      h2: { fontWeight: 500, fontSize: "2rem", lineHeight: 1.2, letterSpacing: "-0.015em" },
      h3: { fontWeight: 500, fontSize: "1.375rem", lineHeight: 1.25, letterSpacing: "-0.01em" },
      h4: { fontWeight: 500, fontSize: "1.125rem", lineHeight: 1.3 },
      h5: { fontWeight: 500 },
      h6: { fontWeight: 500 },
      subtitle1: { fontWeight: 500 },
      subtitle2: { fontWeight: 500 },
      button: { fontWeight: 500, letterSpacing: 0, textTransform: "none" },
      body1: { fontSize: "1rem", lineHeight: 1.6 },
      body2: { fontSize: "0.875rem", lineHeight: 1.6 },
      caption: { fontSize: "0.8125rem", lineHeight: 1.4 },
      // NOTE: the uppercase "eyebrow" pattern (700, 0.08em, caps) is the ONE preserved
      // exception (spec §5.4). It is applied per-component, not via a base variant.
    },
    shape: {
      borderRadius: 10,
    },
    shadows: [
      "none",
      shadowSm,
      shadowSm,
      shadowMd,
      shadowMd,
      shadowLg,
      shadowLg,
      shadowLg,
      shadowXl,
      shadowXl,
      shadowXl,
      shadowXl,
      shadowXl,
      shadowXl,
      shadowXl,
      shadowXl,
      shadowXl,
      shadowXl,
      shadowXl,
      shadowXl,
      shadowXl,
      shadowXl,
      shadowXl,
      shadowXl,
      shadowXl,
    ],
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 500, // brief §5: two weights only (was 600)
            borderRadius: 8,
            transition: "all 0.15s ease",
          },
          contained: {
            boxShadow: shadowSm,
            "&:hover": {
              boxShadow: shadowMd,
              transform: "translateY(-1px)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
          },
          // Force the AA CTA background: contained primary uses coral-600 in BOTH
          // modes (white text 4.92:1, spec R1). primary.main is the lighter
          // coral-as-text value, so override the button bg explicitly.
          containedPrimary: {
            backgroundColor: coral[600],
            "&:hover": { backgroundColor: coral[700] },
          },
          // Transitional: existing color="secondary" contained buttons also get the
          // AA coral-600 bg (4.92:1) until the migration consolidates them onto primary.
          containedSecondary: {
            backgroundColor: coral[600],
            "&:hover": { backgroundColor: coral[700] },
          },
          outlined: {
            "&:hover": {
              transform: "translateY(-1px)",
            },
          },
          sizeLarge: {
            padding: "12px 28px",
            fontSize: "1rem",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: shadowMd,
            borderRadius: 12,
            transition: "box-shadow 0.2s ease, transform 0.2s ease",
            ...(isDark && {
              border: `1px solid ${alpha("#e6edf3", 0.06)}`,
            }),
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            borderRadius: 8,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none", // Remove MUI default gradient overlay
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              transition: "box-shadow 0.15s ease",
              "&.Mui-focused": {
                boxShadow: `0 0 0 3px ${alpha(coral[500], isDark ? 0.3 : 0.2)}`,
              },
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-notchedOutline": {
              transition: "border-color 0.15s ease",
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 10,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&.MuiTableRow-hover:hover": {
              backgroundColor: alpha(isDark ? "#e6edf3" : navy[500], 0.03),
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            marginLeft: 8,
            marginRight: 8,
            "&.Mui-selected": {
              backgroundColor: alpha(coral[500], isDark ? 0.16 : 0.08),
              "&:hover": {
                backgroundColor: alpha(coral[500], isDark ? 0.22 : 0.12),
              },
            },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: "none",
            boxShadow: isDark
              ? "1px 0 0 0 rgba(255,255,255,0.04)"
              : "1px 0 0 0 rgba(0,0,0,0.04)",
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: isDark
              ? "0 1px 0 0 rgba(255,255,255,0.06)"
              : "0 1px 0 0 rgba(0,0,0,0.06)",
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 500,
          },
        },
      },
    },
  });
}

// ---------------------------------------------------------------------------
// MUI module augmentation — type the custom palette keys (spec §5.6)
// ---------------------------------------------------------------------------
declare module "@mui/material/styles" {
  interface Palette {
    identity: Palette["primary"];
    coralFill: string;
    product: typeof import("./tokens").product;
    tint: ReturnType<typeof import("./tokens").tints>;
  }
  interface PaletteOptions {
    identity?: PaletteOptions["primary"];
    coralFill?: string;
    product?: typeof import("./tokens").product;
    tint?: ReturnType<typeof import("./tokens").tints>;
  }
}
declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    identity: true;
  }
}

// Default export for backward compatibility (light mode)
const theme = buildTheme("light");
export default theme;
